/**
 * useTcfOutcomes - Data fetching hook for TCF outcomes
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterConfig } from '../../../records/filter';
import type {
  CauseReportOutcomes,
  ConsumerDutyOutcomeData,
  OutcomeSignals,
  OutcomeTopCause,
  TcfOutcomeId,
  UseOutcomesReturn,
} from '../types';
import { tcfOutcomesKeys, CAUSE_REPORT_STALE_TIME } from '../constants';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams } from '../../cause-report/utils';

interface OutcomeTopCauseApi {
  label_id: string;
  label_name: string;
  count: number;
  percentage: number;
}

interface OutcomeSignalsApi {
  dissatisfaction: number;
  dissatisfaction_percent: number;
  customer_strain: number;
  customer_strain_percent: number;
  foreseeable_harm: number;
  foreseeable_harm_percent: number;
}

interface OutcomeApi {
  id: TcfOutcomeId;
  name: string;
  description: string;
  record_count: number;
  avg_conduct_score: number;
  top_causes: OutcomeTopCauseApi[];
  delta: number;
  volume_delta: number;
  volume_delta_percent: number;
  conduct_score_delta: number;
  signals?: OutcomeSignalsApi;
}

interface OutcomesApiResponse {
  outcomes: OutcomeApi[];
  total_records_with_outcome: number;
}

function transformSignals(signals?: OutcomeSignalsApi): OutcomeSignals | undefined {
  if (!signals) return undefined;
  return {
    dissatisfaction: signals.dissatisfaction,
    dissatisfactionPercent: signals.dissatisfaction_percent,
    customerStrain: signals.customer_strain,
    customerStrainPercent: signals.customer_strain_percent,
    foreseeableHarm: signals.foreseeable_harm,
    foreseeableHarmPercent: signals.foreseeable_harm_percent,
  };
}

function transformOutcomes(data: OutcomesApiResponse): CauseReportOutcomes {
  return {
    outcomes: data.outcomes.map((outcome): ConsumerDutyOutcomeData => ({
      // TCF outcomes reuse the ConsumerDutyOutcomeData shape; ids are the six
      // fixed TCF ids at runtime.
      id: outcome.id as ConsumerDutyOutcomeData['id'],
      name: outcome.name,
      description: outcome.description,
      recordCount: outcome.record_count,
      avgConductScore: outcome.avg_conduct_score,
      topCauses: outcome.top_causes.map((cause): OutcomeTopCause => ({
        labelId: cause.label_id,
        labelName: cause.label_name,
        count: cause.count,
        percentage: cause.percentage,
      })),
      delta: outcome.delta,
      volumeDelta: outcome.volume_delta,
      volumeDeltaPercent: outcome.volume_delta_percent,
      conductScoreDelta: outcome.conduct_score_delta,
      signals: transformSignals(outcome.signals),
    })),
    totalRecordsWithOutcome: data.total_records_with_outcome,
  };
}

async function fetchOutcomes(
  datasetId: string,
  params: Record<string, string>
): Promise<OutcomesApiResponse> {
  const queryString = new URLSearchParams(params).toString();

  return fetchWithAuth(
    `/v1/datasets/${datasetId}/tcf-report/outcomes?${queryString}`
  );
}

export function useTcfOutcomes(
  datasetId: string,
  filters: FilterConfig
): UseOutcomesReturn {
  const filterParams = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useQuery({
    queryKey: tcfOutcomesKeys.list(datasetId, filterParams),
    queryFn: () => fetchOutcomes(datasetId, filterParams),
    select: transformOutcomes,
    staleTime: CAUSE_REPORT_STALE_TIME,
    enabled: !!datasetId,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
